import { QueryCache, MutationCache, QueryClient, Query, Mutation } from '@tanstack/react-query';
import { analytics } from '@app/app/analytics';
import { PlatformName } from '@app/app/analytics/types';

interface ErrorWithMetadata extends Error {
  _metadata?: {
    url: string;
    method: string;
    statusCode: number | null;
  };
}

const handleQueryError = (
  error: Error,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars,no-unused-vars
  action: Query<unknown, unknown, unknown> | Mutation<unknown, unknown, unknown>,
): void => {
  console.error('TanStack Query Error:', {
    message: error.message,
    timestamp: new Date().toISOString(),
    error,
  });

  // Извлекаем метаданные из error (добавлены в apiClient интерцепторе)
  const errorWithMetadata = error as ErrorWithMetadata;
  const metadata: { url?: string; method?: string; statusCode?: number | null } =
    errorWithMetadata._metadata || {};
  const url = metadata.url || 'unknown';
  const method = metadata.method || 'unknown';
  const statusCode = metadata.statusCode || null;

  // Проверяем, является ли это серверной ошибкой с кодом
  let errorCode = 'UNKNOWN_ERROR';
  let hasStructuredErrorWithCode = false;
  let errorMessage = error.message || 'Unknown error';

  // Проверяем наличие структурированной ошибки с кодом
  if (error && typeof error === 'object' && 'result' in error) {
    const serverError = error as { result?: { code?: string } | null; errors?: string };

    // Структурированная ошибка считается только если result не null и имеет code
    if (
      serverError.result &&
      typeof serverError.result === 'object' &&
      'code' in serverError.result
    ) {
      errorCode = serverError.result.code || 'UNKNOWN_ERROR';
      hasStructuredErrorWithCode = true;
    }

    // Извлекаем сообщение об ошибке из структурированной ошибки
    if (serverError.errors) {
      errorMessage = String(serverError.errors);
    }
  }

  // Формируем детальное сообщение для аналитики
  const detailedMessage = {
    error: errorMessage,
    url,
    method,
    statusCode,
    errorDetails: error,
  };

  // Если это структурированная ошибка с кодом, проверяем критичность
  if (hasStructuredErrorWithCode) {
    const criticalErrorCodes = ['UNEXPECTED_ERROR', 'GENERIC_ERROR'];
    if (criticalErrorCodes.includes(errorCode)) {
      analytics.emitEvent('service_error_system', {
        [PlatformName.GTM]: {
          action: 'TanStack Query Error',
          httpCode: statusCode ?? undefined,
          url: url,
          method: method,
          message: JSON.stringify(detailedMessage),
          errorCode: errorCode,
        },
      });
    }
  } else {
    // Для неструктурированных ошибок (включая result: null) всегда отправляем аналитику
    analytics.emitEvent('service_error_system', {
      [PlatformName.GTM]: {
        action: 'TanStack Query Error',
        httpCode: statusCode ?? undefined,
        url: url,
        method: method,
        message: JSON.stringify(detailedMessage),
        errorCode: errorCode,
      },
    });
  }
};

const queryCache = new QueryCache({
  onError: (error, query) => handleQueryError(error, query),
});

const mutationCache = new MutationCache({
  onError: (error, _, __, mutation) => handleQueryError(error, mutation),
});

export const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});
